import React, { useState, useEffect } from 'react';
import { Audit, AuditFinding } from '@/firebase/auditService';
import { auditService } from '@/firebase/auditService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/constants/roles';
import { Copy, Eye, TrendingUp, TrendingDown, Minus, Calendar, CheckCircle, XCircle, AlertCircle, FilePlus, Trash2, ListChecks, FileText, BarChart3 } from 'lucide-react';
import CreateTemplateFromAuditModal from './CreateTemplateFromAuditModal';
import { auditReadinessService } from '@/services/auditReadinessService';
import { getTasksForOrganization } from '@/services/taskService';

interface AuditHistoryViewProps {
  currentAudit: Audit;
  onCopyFinding?: (finding: AuditFinding) => void;
}

const AuditHistoryView: React.FC<AuditHistoryViewProps> = ({
  currentAudit,
  onCopyFinding
}) => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [previousAudits, setPreviousAudits] = useState<Audit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPreviousAudit, setSelectedPreviousAudit] = useState<Audit | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [deletingAuditId, setDeletingAuditId] = useState<string | null>(null);
  const [auditToDelete, setAuditToDelete] = useState<Audit | null>(null);
  const [auditMetadata, setAuditMetadata] = useState<Record<string, {
    tasksCompleted: number;
    totalTasks: number;
    evidenceCount: number;
    readinessScore: number;
  }>>({});
  
  // Check if user is admin
  const userRole = user?.role || '';
  const isUserAdmin = isAdmin(userRole);
  const [auditForTemplateCreation, setAuditForTemplateCreation] = useState<Audit | null>(null);

  useEffect(() => {
    if (currentAudit.auditTemplateId && currentAudit.locationId && currentOrganization?.id) {
      loadPreviousAudits();
    }
  }, [currentAudit.auditTemplateId, currentAudit.locationId, currentOrganization?.id]);

  useEffect(() => {
    if (previousAudits.length > 0) {
      loadAuditMetadata();
    }
  }, [previousAudits]);

  const loadPreviousAudits = async () => {
    if (!currentOrganization?.id) return;

    setIsLoading(true);
    try {
      const result = await auditService.getAuditsForOrganization(currentOrganization.id);
      if (result.success && result.data) {
        // Filter audits: same template, same location, completed, different from current
        const filtered = result.data.filter(audit =>
          audit.auditTemplateId === currentAudit.auditTemplateId &&
          audit.locationId === currentAudit.locationId &&
          audit.status === 'completed' &&
          audit.id !== currentAudit.id
        );

        // Sort by completion date (most recent first)
        filtered.sort((a, b) => {
          const dateA = a.completedAt?.toMillis?.() || a.completedAt || 0;
          const dateB = b.completedAt?.toMillis?.() || b.completedAt || 0;
          return dateB - dateA;
        });

        setPreviousAudits(filtered);
      }
    } catch (error: any) {
      console.error('Error loading previous audits:', error);
      showNotification(`Error loading history: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAuditMetadata = async () => {
    const metadata: Record<string, {
      tasksCompleted: number;
      totalTasks: number;
      evidenceCount: number;
      readinessScore: number;
    }> = {};

    // Load metadata for all previous audits
    for (const audit of previousAudits) {
      if (!audit.id) continue;

      try {
        // Calculate readiness score
        const readiness = await auditReadinessService.calculateReadiness(audit);
        
        // Get task counts (already filtered by organization on server side)
        const orgTasks = await getTasksForOrganization(audit.organizationId);
        const relevantTasks = orgTasks.filter(task => 
          task.locationId === audit.locationId &&
          (audit.complianceTemplateIds?.includes(task.templateId) || 
           audit.associatedTaskIds?.includes(task.id))
        );
        
        const tasksCompleted = relevantTasks.filter(task => task.status === 'completed').length;
        const totalTasks = relevantTasks.length;
        const evidenceCount = audit.evidence?.length || 0;

        metadata[audit.id] = {
          tasksCompleted,
          totalTasks,
          evidenceCount,
          readinessScore: readiness.overallScore
        };
      } catch (error) {
        console.error(`Error loading metadata for audit ${audit.id}:`, error);
        // Set default values on error
        metadata[audit.id] = {
          tasksCompleted: 0,
          totalTasks: 0,
          evidenceCount: audit.evidence?.length || 0,
          readinessScore: audit.completionPercentage || 0
        };
      }
    }

    setAuditMetadata(metadata);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const config = {
      compliant: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Compliant' },
      'non-compliant': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Non-Compliant' },
      partial: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Partial' },
      not_applicable: { color: 'bg-gray-100 text-gray-800', icon: Minus, label: 'N/A' },
    };

    const statusConfig = config[status as keyof typeof config] || config.compliant;
    const Icon = statusConfig.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="w-3 h-3" />
        {statusConfig.label}
      </span>
    );
  };

  const calculateTrend = (current: number, previous: number): { direction: 'up' | 'down' | 'same', value: number } => {
    const diff = current - previous;
    if (diff > 0) return { direction: 'up', value: diff };
    if (diff < 0) return { direction: 'down', value: Math.abs(diff) };
    return { direction: 'same', value: 0 };
  };

  const handleDeleteAudit = async (audit: Audit) => {
    if (!audit.id) return;

    setDeletingAuditId(audit.id);
    try {
      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.deleteAudit(audit.id, userInfo);

      if (result.success) {
        showNotification('Audit deleted successfully', 'success');
        // Remove from local state
        setPreviousAudits(prev => prev.filter(a => a.id !== audit.id));
        setAuditToDelete(null);
        // Reload to refresh the list
        await loadPreviousAudits();
      } else {
        showNotification(`Error deleting audit: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting audit:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setDeletingAuditId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading audit history...</span>
        </div>
      </div>
    );
  }

  if (previousAudits.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Audit History</h2>
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No previous audits found for this template and location.</p>
        </div>
      </div>
    );
  }

  const mostRecentAudit = previousAudits[0];
  const scoreTrend = calculateTrend(
    currentAudit.completionPercentage || 0,
    mostRecentAudit.completionPercentage || 0
  );

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Audit History</h2>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Previous Audits</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1">{previousAudits.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Most Recent</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {formatDate(mostRecentAudit.completedAt)}
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Score Trend</div>
            <div className="flex items-center gap-2 mt-1">
              {scoreTrend.direction === 'up' && (
                <>
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="text-lg font-semibold text-green-600">+{scoreTrend.value}%</span>
                </>
              )}
              {scoreTrend.direction === 'down' && (
                <>
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  <span className="text-lg font-semibold text-red-600">-{scoreTrend.value}%</span>
                </>
              )}
              {scoreTrend.direction === 'same' && (
                <>
                  <Minus className="w-5 h-5 text-gray-600" />
                  <span className="text-lg font-semibold text-gray-600">No change</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Comparison Toggle */}
        {mostRecentAudit && (
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => {
                setSelectedPreviousAudit(mostRecentAudit);
                setShowComparison(!showComparison);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {showComparison ? 'Hide Comparison' : 'Compare with Most Recent'}
            </button>
          </div>
        )}
      </div>

      {/* Comparison View */}
      {showComparison && selectedPreviousAudit && (
        <AuditComparisonView
          currentAudit={currentAudit}
          previousAudit={selectedPreviousAudit}
          onCopyFinding={onCopyFinding}
        />
      )}

      {/* Previous Audits List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Previous Audits</h3>
        <div className="space-y-3">
          {previousAudits.map((audit) => (
            <div key={audit.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{formatDate(audit.completedAt)}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="font-medium">Readiness:</span>{' '}
                        {auditMetadata[audit.id || '']?.readinessScore ?? audit.completionPercentage ?? 0}%
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ListChecks className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="font-medium">Tasks:</span>{' '}
                        {auditMetadata[audit.id || '']?.tasksCompleted ?? 0} / {auditMetadata[audit.id || '']?.totalTasks ?? 0}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="font-medium">Evidence:</span>{' '}
                        {auditMetadata[audit.id || '']?.evidenceCount ?? audit.evidence?.length ?? 0}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="font-medium">Findings:</span>{' '}
                        {audit.findings?.length || 0}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (audit.id) {
                        window.open(`/audit/${audit.id}`, '_blank');
                      }
                    }}
                    className="px-3 py-1 text-sm text-primary-600 hover:text-primary-800 border border-primary-200 rounded-md hover:bg-primary-50"
                  >
                    View Full
                  </button>
                  <button
                    onClick={() => {
                      setSelectedPreviousAudit(audit);
                      setShowComparison(true);
                    }}
                    className="px-3 py-1 text-sm text-gray-700 hover:text-gray-900 border border-gray-200 rounded-md hover:bg-gray-50"
                  >
                    Compare
                  </button>
                  {audit.status === 'completed' && (
                    <button
                      onClick={() => setAuditForTemplateCreation(audit)}
                      className="px-3 py-1 text-sm text-green-700 hover:text-green-900 border border-green-200 rounded-md hover:bg-green-50 flex items-center gap-1"
                      title="Create template from this audit"
                    >
                      <FilePlus className="w-3 h-3" />
                      Create Template
                    </button>
                  )}
                  {isUserAdmin && (
                    <button
                      onClick={() => setAuditToDelete(audit)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-200 rounded-md hover:bg-red-50 flex items-center gap-1"
                      disabled={deletingAuditId === audit.id}
                      title="Delete Audit"
                    >
                      {deletingAuditId === audit.id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                      ) : (
                        <>
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trend Analysis */}
      {previousAudits.length >= 2 && (
        <AuditTrendAnalysis audits={[currentAudit, ...previousAudits]} />
      )}

      {/* Create Template Modal */}
      {auditForTemplateCreation && (
        <CreateTemplateFromAuditModal
          audit={auditForTemplateCreation}
          onClose={() => setAuditForTemplateCreation(null)}
          onSuccess={() => {
            setAuditForTemplateCreation(null);
            // Optionally reload or refresh
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {auditToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Audit</h3>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Location:</span> {auditToDelete.locationName}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <span className="font-medium">Scheduled:</span> {formatDate(auditToDelete.scheduledDate)}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <span className="font-medium">Status:</span> {auditToDelete.status}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAuditToDelete(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                disabled={deletingAuditId !== null}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAudit(auditToDelete)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={deletingAuditId !== null}
              >
                {deletingAuditId ? 'Deleting...' : 'Delete Audit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Comparison View Component
interface AuditComparisonViewProps {
  currentAudit: Audit;
  previousAudit: Audit;
  onCopyFinding?: (finding: AuditFinding) => void;
}

const AuditComparisonView: React.FC<AuditComparisonViewProps> = ({
  currentAudit,
  previousAudit,
  onCopyFinding
}) => {
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const config = {
      compliant: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Compliant' },
      'non-compliant': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Non-Compliant' },
      partial: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Partial' },
      not_applicable: { color: 'bg-gray-100 text-gray-800', icon: Minus, label: 'N/A' },
    };

    const statusConfig = config[status as keyof typeof config] || config.compliant;
    const Icon = statusConfig.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="w-3 h-3" />
        {statusConfig.label}
      </span>
    );
  };

  // Get findings by compliance template ID
  const getFindingsByTemplate = (audit: Audit): Map<string, AuditFinding> => {
    const map = new Map<string, AuditFinding>();
    audit.findings?.forEach(finding => {
      map.set(finding.complianceTemplateId, finding);
    });
    return map;
  };

  const currentFindings = getFindingsByTemplate(currentAudit);
  const previousFindings = getFindingsByTemplate(previousAudit);

  // Get all unique template IDs
  const allTemplateIds = new Set([
    ...Array.from(currentFindings.keys()),
    ...Array.from(previousFindings.keys())
  ]);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Comparison: Current vs. {formatDate(previousAudit.completedAt)}
      </h3>

      {/* Overall Metrics Comparison */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Current Audit</div>
          <div className="text-2xl font-semibold text-gray-900">{currentAudit.completionPercentage || 0}%</div>
          <div className="text-xs text-gray-500 mt-1">
            {currentAudit.findings?.length || 0} findings • {currentAudit.evidence?.length || 0} evidence
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Previous Audit</div>
          <div className="text-2xl font-semibold text-gray-900">{previousAudit.completionPercentage || 0}%</div>
          <div className="text-xs text-gray-500 mt-1">
            {previousAudit.findings?.length || 0} findings • {previousAudit.evidence?.length || 0} evidence
          </div>
        </div>
      </div>

      {/* Findings Comparison */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Findings Comparison</h4>
        <div className="space-y-3">
          {Array.from(allTemplateIds).map((templateId) => {
            const currentFinding = currentFindings.get(templateId);
            const previousFinding = previousFindings.get(templateId);
            const hasChange = currentFinding?.status !== previousFinding?.status;

            return (
              <div
                key={templateId}
                className={`border rounded-lg p-3 ${hasChange ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Current</div>
                    {currentFinding ? (
                      <div>
                        {getStatusBadge(currentFinding.status)}
                        {currentFinding.notes && (
                          <p className="text-xs text-gray-700 mt-1 line-clamp-2">{currentFinding.notes}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No finding</span>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Previous</div>
                    {previousFinding ? (
                      <div>
                        {getStatusBadge(previousFinding.status)}
                        {previousFinding.notes && (
                          <p className="text-xs text-gray-700 mt-1 line-clamp-2">{previousFinding.notes}</p>
                        )}
                        {onCopyFinding && !currentFinding && (
                          <button
                            onClick={() => onCopyFinding(previousFinding)}
                            className="mt-2 text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" />
                            Copy Finding
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No finding</span>
                    )}
                  </div>
                </div>
                {hasChange && (
                  <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                    Status changed from {previousFinding?.status || 'none'} to {currentFinding?.status || 'none'}
                  </div>
                )}
              </div>
            );
          })}
          {allTemplateIds.size === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No findings to compare</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Trend Analysis Component
interface AuditTrendAnalysisProps {
  audits: Audit[];
}

const AuditTrendAnalysis: React.FC<AuditTrendAnalysisProps> = ({ audits }) => {
  // Sort audits by date (oldest first for trend)
  const sortedAudits = [...audits].sort((a, b) => {
    const dateA = a.completedAt?.toMillis?.() || a.scheduledDate?.toMillis?.() || 0;
    const dateB = b.completedAt?.toMillis?.() || b.scheduledDate?.toMillis?.() || 0;
    return dateA - dateB;
  });

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const maxScore = Math.max(...sortedAudits.map(a => a.completionPercentage || 0), 100);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Trend Analysis</h3>
      
      {/* Score Trend Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Completion Score Over Time</h4>
        <div className="space-y-3">
          {sortedAudits.map((audit, index) => {
            const score = audit.completionPercentage || 0;
            const date = audit.completedAt || audit.scheduledDate;
            const isCurrent = audit.status !== 'completed';
            
            return (
              <div key={audit.id || index} className="flex items-center gap-4">
                <div className="w-24 text-xs text-gray-600 flex-shrink-0">
                  {formatDate(date)}
                  {isCurrent && <span className="text-primary-600 ml-1">(Current)</span>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full ${
                          score >= 90 ? 'bg-green-500' :
                          score >= 70 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${(score / maxScore) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {score}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Findings Trend */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Findings Count Over Time</h4>
        <div className="space-y-2">
          {sortedAudits.map((audit, index) => {
            const findingsCount = audit.findings?.length || 0;
            const date = audit.completedAt || audit.scheduledDate;
            const isCurrent = audit.status !== 'completed';
            const maxFindings = Math.max(...sortedAudits.map(a => a.findings?.length || 0), 1);

            return (
              <div key={audit.id || index} className="flex items-center gap-4">
                <div className="w-24 text-xs text-gray-600 flex-shrink-0">
                  {formatDate(date)}
                  {isCurrent && <span className="text-primary-600 ml-1">(Current)</span>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full"
                        style={{ width: `${(findingsCount / maxFindings) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {findingsCount}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AuditHistoryView;

